class VideoRenderer {
  constructor(host) {
    this.host = host;
  }
  render() {
    throw new Error("render() must be implemented");
  }
  play() {
  }
  pause() {
  }
  stop() {
  }
  seek() {
  }
}
class NativeVideoRenderer extends VideoRenderer {
  render(src, attrs = {}) {
    const video = document.createElement("video");
    video.classList.add("video");
    video.src = src;
    video.style.width = "100%";
    video.style.height = "100%";
    Object.entries(attrs).forEach(([key, value]) => {
      if (value) {
        video.setAttribute(key, "");
      }
    });
    video.addEventListener("play", () => this.host.dispatchEvent(new Event("play")));
    video.addEventListener("pause", () => this.host.dispatchEvent(new Event("pause")));
    video.addEventListener("ended", () => this.host.dispatchEvent(new Event("ended")));
    video.addEventListener("loadeddata", () => this.host.dispatchEvent(new Event("load")));
    video.addEventListener("canplay", () => this.host.dispatchEvent(new Event("ready")));
    this.video = video;
    return video;
  }
  play() {
    var _a;
    (_a = this.video) == null ? void 0 : _a.play();
  }
  pause() {
    var _a;
    (_a = this.video) == null ? void 0 : _a.pause();
  }
  stop() {
    if (this.video) {
      this.video.pause();
      this.video.currentTime = 0;
    }
  }
  seek(time) {
    if (this.video) {
      this.video.currentTime = time;
    }
  }
}
class YouTubeRenderer extends VideoRenderer {
  render(src, attrs = {}) {
    const id = this._extractID(src);
    const params = new URLSearchParams();
    if (attrs.autoplay) {
      params.set("autoplay", "1");
    }
    if (attrs.muted) {
      params.set("mute", "1");
    }
    const iframe = document.createElement("iframe");
    iframe.classList.add("video");
    iframe.src = `https://www.youtube.com/embed/${id}?enablejsapi=1&${params.toString()}`;
    iframe.allow = "autoplay; encrypted-media";
    iframe.setAttribute("frameborder", "0");
    iframe.setAttribute("allowfullscreen", "");
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    iframe.addEventListener("load", () => {
      this.host.dispatchEvent(new Event("load"));
      this.host.dispatchEvent(new Event("ready"));
    });
    this.iframe = iframe;
    return iframe;
  }
  _extractID(url) {
    const match = url.match(/(?:youtube\.com\/.*[?&]v=|youtu\.be\/|embed\/)([\w-]+)/);
    return match ? match[1] : "";
  }
}
class VimeoRenderer extends VideoRenderer {
  render(src, attrs = {}) {
    const id = this._extractID(src);
    const params = new URLSearchParams();
    if (attrs.autoplay) {
      params.set("autoplay", "1");
    }
    if (attrs.muted) {
      params.set("muted", "1");
    }
    const iframe = document.createElement("iframe");
    iframe.classList.add("video");
    iframe.src = `https://player.vimeo.com/video/${id}?${params.toString()}`;
    iframe.allow = "autoplay; fullscreen";
    iframe.setAttribute("frameborder", "0");
    iframe.setAttribute("allowfullscreen", "");
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    iframe.addEventListener("load", () => {
      this.host.dispatchEvent(new Event("load"));
      this.host.dispatchEvent(new Event("ready"));
    });
    this.iframe = iframe;
    return iframe;
  }
  _extractID(url) {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? match[1] : "";
  }
}
class OnaVideo extends HTMLElement {
  static get observedAttributes() {
    return ["src", "autoplay", "muted", "loop", "controls", "playsinline"];
  }
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._renderer = null;
  }
  connectedCallback() {
    this._render();
  }
  attributeChangedCallback(name, oldVal, newVal) {
    if (name === "src" && oldVal !== newVal) {
      this._render();
    }
  }
  play() {
    var _a;
    (_a = this._renderer) == null ? void 0 : _a.play();
  }
  pause() {
    var _a;
    (_a = this._renderer) == null ? void 0 : _a.pause();
  }
  stop() {
    var _a;
    (_a = this._renderer) == null ? void 0 : _a.stop();
  }
  seek(time) {
    var _a;
    (_a = this._renderer) == null ? void 0 : _a.seek(time);
  }
  _render() {
    const src = this.getAttribute("src");
    if (!src) {
      return;
    }
    const type = this._getSourceType(src);
    const wrapper = document.createElement("div");
    wrapper.style.display = "inline-block";
    const attrs = Object.fromEntries(
      OnaVideo.observedAttributes.filter((attr) => attr !== "src").map((attr) => [attr, this.hasAttribute(attr)])
    );
    if (type === "youtube") {
      this._renderer = new YouTubeRenderer(this);
    } else if (type === "vimeo") {
      this._renderer = new VimeoRenderer(this);
    } else {
      this._renderer = new NativeVideoRenderer(this);
    }
    const content = this._renderer.render(src, attrs);
    content.setAttribute("part", "video");
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-block;
        }

        .video {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      </style>
    `;
    this.shadowRoot.appendChild(content);
  }
  _getSourceType(url) {
    if (/youtube\.com|youtu\.be/.test(url)) {
      return "youtube";
    }
    if (/vimeo\.com/.test(url)) {
      return "vimeo";
    }
    return "video";
  }
}
customElements.define("ona-video", OnaVideo);
